import {
  UpdateInspectionStatus,
  UpdateInspectionStatusResponse,
  DamagesImagesData,
  CreateDamageLineBody,
  UpdateDamageLineBody,
  RegularDamagePart,
  Part,
  FlatRate,
  NotesData,
  UpdateNote,
  UpdateDamageAuthorization,
  BatchUpdateDamageClassificationResult,
  VehicleDamageChangeSummary,
  ElectricHybridData as ChecklistElectricHybridData,
  EngineRunningData as ChecklistEngineRunningData,
  InteriorData as ChecklistInteriorData,
  SummariesData,
  MechanicalsNotesData,
  MechanicalsWeelsData,
  ReportInitialData,
  DamageCodesData,
  TNROverride,
  TNRData,
  AuditLogData,
  DamageCondition,
  AppSearch,
  Inspection,
  AppSearchResponse,
  BatchUpdateDamageStatus,
  InspectionFilters,
  VehicleInspectionInsights,
  MOTData,
  ReplaceImageData,
  UpdateMileageData,
  UpdateRegData,
  UpdateMechanicalInspection,
  Severity,
  RepairMethod,
  FprInspection,
  AddDamageParts,
  Damage,
  AddDamageActions,
  PreTimeSuggestionLine,
  FrCode,
  SupplierCode,
  LabourCode,
  DynamicOperations,
  WorkCenter,
  TNRPreAppraisal,
  TNRPostAppraisal,
} from '@/types';
import {
  Core,
  INSPECTIONS_VEHICLES,
  INSPECTIONS_VEHICLE,
  INSPECTIONS_VEHICLE_DAMAGE_AUTHORIZATION,
  INSPECTIONS_VEHICLE_DAMAGE_PRE_TIMING_REVIEW,
  INSPECTIONS_VEHICLE_WORK_CENTRE,
  INSPECTIONS_VEHICLES_ID_DAMAGES_LINES,
  GET_DAMAGES_LINES,
  BATCH_UPDATE_DAMAGES_LINES,
  DELETE_DAMAGES_LINES,
  INSPECTIONS_VEHICLE_DAMAGE_PARTS,
  INSPECTIONS_VEHICLE_DAMAGE_ACTIONS,
  INSPECTIONS_PRE_TIME_SUGGESTIONS,
  INSPECTIONS_VEHICLES_PARTS,
  DELETE_DAMAGE_PART,
  DELETE_DAMAGE_ACTION,
  INSPECTIONS_FLAT_RATE_CODE,
  INSPECTIONS_VEHICLES_ID_NOTES,
  INSPECTIONS_VEHICLES_NOTES,
  INSPECTIONS_LOGS,
  INSPECTIONS_CHECKLIST_DYNAMIC_OPERATIONS,
  INSPECTIONS_CHECKLIST_ELECTRIC_HYBRID,
  INSPECTIONS_CHECKLIST_ENGINE_RUNNING,
  INSPECTIONS_CHECKLIST_INTERIOR,
  INSPECTION_DAMAGES_IMAGES,
  INSPECTION_DAMAGES_IMAGES_ID,
  INSPECTIONS_VEHICLES_SUMMARIES,
  INSPECTIONS_VEHICLES_MECHANICALS_NOTES,
  INSPECTIONS_MECHANICALS_WHEELS,
  INSPECTIONS_REPORTS_INITIAL,
  INSPECTIONS_VEHICLE_DAMAGES_CODES_SITE_PART,
  INSPECTIONS_VEHICLE_DAMAGES_CONDITION_CODES_PART,
  UN_DELETE_DAMAGES_LINES,
  INSPECTION_TNROVERRIDE,
  INSPECTION_UPDATE_MECHANICALINSPECTION,
  INSPECTION_GETTNRDETAILS,
  INSPECTIONS_VEHICLE_DAMAGE_STATUS,
  INSPECTIONS_VEHICLES_FILTERS,
  INSPECTIONS_VEHICLES_INSIGHTS,
  INSPECTIONS_MOT,
  INSPECTION_REPLACE_IMAGE,
  INSPECTION_UPDATE_MILEAGE,
  INSPECTION_UPDATE_REG,
  INSPECTION_UPDATE_TNR_PRE_APPRAISAL,
  INSPECTION_UPDATE_TNR_POST_APPRAISAL,
  INSPECTIONS_VEHICLE_SEVERITY_CODES_PART_TYPE,
  INSPECTIONS_VEHICLE_REPAIR_ACTION_PART_TYPE_SEVERITY,
  INSPECTIONS_VEHICLE_DAMAGE_APPROVE,
  INSPECTIONS_VEHICLE_DAMAGE_CLASSIFICATION_CHANGE_SUMMARY,
  FPR_INSPECTIONS,
  INSPECTIONS_LABOUR_CODE,
  INSPECTIONS_SUPPLIER_CODE,
  INSPECTIONS_SUPPLIER_PART_CODES,
} from '../Core';

type UploadDamageImageData = {
  data: {
    vehicleDamageId: number;
    imageFileName: string;
    imageFileBase64: ArrayBuffer | string | null;
  };
};
type DeleteDamageLineData = {
  damageLineId: number;
  inspectionId: number;
};

class Inspections extends Core {
  getInspectionsReportsInitial = (inspectionId: number) =>
    this.get<ReportInitialData>(INSPECTIONS_REPORTS_INITIAL(inspectionId));

  getInspectionsVehiclesFilters = async () =>
    this.get<InspectionFilters>(INSPECTIONS_VEHICLES_FILTERS());

  postInspectionsVehicles = async (
    body: AppSearch<Inspection> & {
      includeDamages?: boolean;
      insightCard?: string;
      insightAgeBand?: string;
    },
  ) =>
    this.post<AppSearchResponse<Inspection> & { damages?: Damage[] }>(
      INSPECTIONS_VEHICLES(),
      body,
    );

  postInspectionsVehiclesInsights = async (
    body: AppSearch<Inspection> & {
      scope?: string;
      chartPeriodDays?: number;
    },
  ) =>
    this.post<VehicleInspectionInsights>(INSPECTIONS_VEHICLES_INSIGHTS(), body);

  updateInspectionsVehicle = async (data: UpdateInspectionStatus) =>
    this.patch<UpdateInspectionStatusResponse>(INSPECTIONS_VEHICLES(), data);

  getInspectionsVehicle = async (id: number) =>
    this.get<Inspection>(INSPECTIONS_VEHICLE(id));

  getInspectionsVehiclesDamagesLines = async (id: number) =>
    this.get<Damage[]>(INSPECTIONS_VEHICLES_ID_DAMAGES_LINES(id));

  getInspectionsVehicleDamageCodes = async (
    id: number,
    siteId: number | string,
  ) =>
    this.get<DamageCodesData>(
      INSPECTIONS_VEHICLE_DAMAGES_CODES_SITE_PART(id, siteId),
    );

  getInspectionsVehicleDamageConditionCodes = async (
    id: number,
    partCode: number | string,
  ) =>
    this.get<DamageCondition[]>(
      INSPECTIONS_VEHICLE_DAMAGES_CONDITION_CODES_PART(id, partCode),
    );

  getInspectionsVehicleSeverityCodes = async (
    id: number,
    partCode: number | string,
    typeCode: string | number,
  ) =>
    this.get<Severity[]>(
      INSPECTIONS_VEHICLE_SEVERITY_CODES_PART_TYPE(id, partCode, typeCode),
    );

  getInspectionsVehicleRepairActionCodes = async (
    id: number,
    partCode: number | string,
    typeCode: string | number,
    severityId: number,
  ) =>
    this.get<RepairMethod[]>(
      INSPECTIONS_VEHICLE_REPAIR_ACTION_PART_TYPE_SEVERITY(
        id,
        partCode,
        typeCode,
        severityId,
      ),
    );

  updateDamageAuthorization = async (data: UpdateDamageAuthorization) =>
    this.patch<boolean>(INSPECTIONS_VEHICLE_DAMAGE_AUTHORIZATION(), data);

  resolvePreTimingReview = async (data: {
    inspectionId: number;
    damageLineId: number;
    keepPreTiming: boolean;
  }) =>
    this.patch<number>(INSPECTIONS_VEHICLE_DAMAGE_PRE_TIMING_REVIEW(), data);

  getVehicleDamageClassificationChangeSummary = async (inspectionId: number) =>
    this.get<VehicleDamageChangeSummary[]>(
      INSPECTIONS_VEHICLE_DAMAGE_CLASSIFICATION_CHANGE_SUMMARY(inspectionId),
    );

  getInspectionsVehicleWorkCentres = async (inspectionId: number) =>
    this.patch<WorkCenter[]>(INSPECTIONS_VEHICLE_WORK_CENTRE(inspectionId));

  batchUpdateInspectionsVehicleDamagesStatus = async (
    data: BatchUpdateDamageStatus,
  ) => this.patch<boolean>(INSPECTIONS_VEHICLE_DAMAGE_STATUS(), data);

  updateInspectionVehicleDamageDefaultStatus = async (inspectionId: number) =>
    this.patch<boolean>(INSPECTIONS_VEHICLE_DAMAGE_APPROVE(), { inspectionId });

  getInspectionsVehicleDamageParts = async (inspectionId: number) =>
    this.get<RegularDamagePart[]>(
      INSPECTIONS_VEHICLE_DAMAGE_PARTS(inspectionId),
    );

  addInspectionsVehicleDamageParts = async (data: AddDamageParts) =>
    this.post(INSPECTIONS_VEHICLE_DAMAGE_PARTS(), data);

  addInspectionsVehicleDamageActions = async (data: AddDamageActions) =>
    this.post(INSPECTIONS_VEHICLE_DAMAGE_ACTIONS(), data);

  getPreTimeSuggestions = async (inspectionId: number) =>
    this.get<PreTimeSuggestionLine[]>(
      INSPECTIONS_PRE_TIME_SUGGESTIONS(inspectionId),
    );

  getInspectionsVehiclesParts = async (data: {
    inspectionId: number;
    partNumber: string;
    type: string;
  }) => this.get<Part[]>(INSPECTIONS_VEHICLES_PARTS(), data);

  getInspectionsVehiclesFlatRateCode = async (data: {
    inspectionId: number;
    flatRateCode: string;
  }) => this.get<FrCode[]>(INSPECTIONS_FLAT_RATE_CODE(), data);

  getInspectionsVehiclesLabour = async (inspectionId: number) =>
    this.get<LabourCode[]>(INSPECTIONS_LABOUR_CODE(), { inspectionId });

  getInspectionsVehiclesSupplier = async () =>
    this.get<SupplierCode[]>(INSPECTIONS_SUPPLIER_CODE());

  getInspectionsVehiclesSupplierPart = async (data: {
    inspectionId: number;
    supplierCode: string;
    partNumber: string;
  }) => this.get<Part[]>(INSPECTIONS_SUPPLIER_PART_CODES(), data);

  deleteInspectionsVehiclesDamagePart = async ({
    partNumberId,
    inspectionId,
  }: {
    partNumberId: number;
    inspectionId: number;
  }) => this.delete(DELETE_DAMAGE_PART(partNumberId, inspectionId));

  deleteInspectionsVehiclesDamageAction = async (damageActionId: number) =>
    this.delete(DELETE_DAMAGE_ACTION(damageActionId));

  createInspectionsVehiclesFlatRateCode = async (data: FlatRate) =>
    this.post<number>(INSPECTIONS_FLAT_RATE_CODE(), data);

  createInspectionsVehiclesDamagesLines = async (data: CreateDamageLineBody) =>
    this.post<number>(GET_DAMAGES_LINES(), data);

  batchUpdateInspectionsVehiclesDamagesLines = async (data: {
    inspectionId: number;
    items: UpdateDamageLineBody[];
  }) =>
    this.put<BatchUpdateDamageClassificationResult>(
      BATCH_UPDATE_DAMAGES_LINES(),
      data,
    );

  deleteInspectionsVehiclesDamagesLines = async ({
    damageLineId,
    inspectionId,
  }: DeleteDamageLineData) =>
    this.delete<number>(DELETE_DAMAGES_LINES(damageLineId, inspectionId));

  unDeleteInspectionsVehiclesDamagesLines = async ({
    damageLineId,
    inspectionId,
  }: DeleteDamageLineData) =>
    this.post<number>(UN_DELETE_DAMAGES_LINES(damageLineId, inspectionId));

  getInspectionsVehiclesNotes = async (id: number) =>
    this.get<NotesData>(INSPECTIONS_VEHICLES_ID_NOTES(id));

  updateInspectionsVehiclesNotes = async (data: UpdateNote) =>
    this.patch<number>(INSPECTIONS_VEHICLES_NOTES(), data);

  getInspectionsLogs = async (id: number) =>
    this.get<AuditLogData>(INSPECTIONS_LOGS(id));

  getInspectionsChecklistDynamicOperations = async (id: number) =>
    this.get<DynamicOperations[]>(INSPECTIONS_CHECKLIST_DYNAMIC_OPERATIONS(id));

  getInspectionsChecklistElectricHybrid = async (id: number) =>
    this.get<ChecklistElectricHybridData>(
      INSPECTIONS_CHECKLIST_ELECTRIC_HYBRID(id),
    );

  getInspectionsChecklistEngineRunning = async (id: number) =>
    this.get<ChecklistEngineRunningData>(
      INSPECTIONS_CHECKLIST_ENGINE_RUNNING(id),
    );

  getInspectionsChecklistInterior = async (id: number) =>
    this.get<ChecklistInteriorData>(INSPECTIONS_CHECKLIST_INTERIOR(id));

  getInspectionsDamagesImages = async (id: number) =>
    this.get<DamagesImagesData>(INSPECTION_DAMAGES_IMAGES(id));

  uploadInspectionsDamagesImages = async ({ data }: UploadDamageImageData) =>
    this.post(INSPECTION_DAMAGES_IMAGES(), data);

  deleteInspectionsDamagesImage = async (id: number) =>
    this.delete(INSPECTION_DAMAGES_IMAGES_ID(id));

  getInspectionsVehiclesSummaries = async (id: number) =>
    this.get<SummariesData>(INSPECTIONS_VEHICLES_SUMMARIES(id));

  getInspectionsVehiclesMechanicalsNotes = async (id: number) =>
    this.get<MechanicalsNotesData>(INSPECTIONS_VEHICLES_MECHANICALS_NOTES(id));

  getInspectionsMechanicalsWheels = async (id: number) =>
    this.get<MechanicalsWeelsData>(INSPECTIONS_MECHANICALS_WHEELS(id));

  postTNROverride = async (body: TNROverride) =>
    this.post(INSPECTION_TNROVERRIDE(), body);

  updateTNRPreAppraisal = async (body: TNRPreAppraisal) =>
    this.patch(INSPECTION_UPDATE_TNR_PRE_APPRAISAL(), body);

  updateTNRPostAppraisal = async (body: TNRPostAppraisal) =>
    this.patch(INSPECTION_UPDATE_TNR_POST_APPRAISAL(), body);

  getTNRDetails = async (id: number) =>
    this.get<TNRData>(INSPECTION_GETTNRDETAILS(id));

  getInspectionMOT = async (id: number) =>
    this.get<MOTData>(INSPECTIONS_MOT(id));

  postReplaceImage = async (body: ReplaceImageData) =>
    this.post(INSPECTION_REPLACE_IMAGE(), body);

  updateInspectionMileage = async (body: UpdateMileageData) =>
    this.patch(INSPECTION_UPDATE_MILEAGE(), body);

  updateInspectionReg = async (body: UpdateRegData) =>
    this.patch(INSPECTION_UPDATE_REG(), body);

  updateInspectionMechanicalInspection = async (
    body: UpdateMechanicalInspection,
  ) => this.patch(INSPECTION_UPDATE_MECHANICALINSPECTION(), body);

  getFprInspectionDetail = async (vin: string) =>
    this.get<FprInspection[]>(FPR_INSPECTIONS(vin));
}

export const inspections = new Inspections();
